import instance from "./BasebReviewsApi";

/**
 * Check if Google Reviews integration is active for an organisation
 */
export function getGoogleReviewsStatus(organisationId) {
  return instance.request({
    url: `status/?organisation_id=${organisationId}`,
    method: "GET",
    withCredentials: false,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  });
}

export function syncGoogleReviews(data) {
  return instance.post("sync/", data, { withCredentials: false });
}

/**
 * Check if TripAdvisor Reviews integration is active for an organisation
 */
export function getTripadvisorReviewsStatus(organisationId) {
  return instance.request({
    url: `tripadvisor/status/?organisation_id=${organisationId}`,
    method: "GET",
    withCredentials: false,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  });
}

/**
 * Save TripAdvisor location mappings between b-connect and TripAdvisor url_path
 */
export function saveTripadvisorLocationMappings(data) {
  return instance.request({
    url: `tripadvisor/save-mappings/`,
    method: "POST",
    withCredentials: false,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    data: data,
  });
}

/**
 * Get existing TripAdvisor mappings
 */
export function getTripadvisorLocationMappings(organisationId) {
  return instance.request({
    url: `tripadvisor/mappings/?organisation_id=${organisationId}`,
    method: "GET",
    withCredentials: false,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  });
}

/**
 * Deactivate TripAdvisor Reviews integration
 */
export function deactivateTripadvisorReviews(data) {
  return instance.request({
    url: `tripadvisor/deactivate/`,
    method: "POST",
    withCredentials: false,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    data: data,
  });
}

export function getCompetitors({ organisationId, locationId }) {
  const query = new URLSearchParams({
    organisation_id: organisationId,
    location_id: locationId,
  });
  return instance.get(`competitors/?${query.toString()}`, { withCredentials: false });
}

export function configureCompetitors(data) {
  return instance.post("competitors/configure/", data, { withCredentials: false });
}

export function refreshCompetitors(data) {
  return instance.post("competitors/refresh/", data, { withCredentials: false });
}

export function updateCompetitorTracking(competitorId, data) {
  return instance.patch(`competitors/${competitorId}/track/`, data, { withCredentials: false });
}

/**
 * Initiate OAuth flow - returns the Google OAuth URL
 */
export function initiateGoogleOAuth(data) {
  return instance.request({
    url: `initiate-oauth/`,
    method: "POST",
    withCredentials: false,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    data: data,
  });
}

/**
 * Handle OAuth callback - exchange code for tokens
 */
export function handleGoogleOAuthCallback(data) {
  return instance.request({
    url: `oauth-callback/`,
    method: "POST",
    withCredentials: false,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    data: data,
  });
}

/**
 * Fetch Google Business Profile locations for the authenticated account
 */
export function fetchGoogleLocations(organisationId) {
  return instance.request({
    url: `fetch-google-locations/?organisation_id=${organisationId}`,
    method: "GET",
    withCredentials: false,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  });
}

/**
 * Save location mappings between b-connect and Google Business Profile
 */
export function saveGoogleLocationMappings(data) {
  return instance.request({
    url: `save-mappings/`,
    method: "POST",
    withCredentials: false,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    data: data,
  });
}

/**
 * Get existing location mappings
 */
export function getGoogleLocationMappings(organisationId) {
  return instance.request({
    url: `mappings/?organisation_id=${organisationId}`,
    method: "GET",
    withCredentials: false,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  });
}

/**
 * Get the live Google Business Profile for a mapped b-connect location.
 */
export function getGoogleBusinessProfile(organisationId, locationId) {
  return instance.request({
    url: `locations/${locationId}/business-profile/?organisation_id=${organisationId}`,
    method: "GET",
    withCredentials: false,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  });
}

/**
 * Search Google Business Profile categories for a location's region.
 */
export function getGoogleBusinessProfileCategories({
  organisationId,
  locationId,
  regionCode,
  languageCode = "en",
  search = "",
}) {
  const query = new URLSearchParams({
    organisation_id: organisationId,
    region_code: regionCode,
    language_code: languageCode,
  });
  if (search) query.set("search", search);

  return instance.request({
    url: `locations/${locationId}/business-profile/categories/?${query.toString()}`,
    method: "GET",
    withCredentials: false,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  });
}

/**
 * Update the Basic Information card and persist it to Google.
 */
export function updateGoogleBusinessProfileBasicInformation(locationId, data) {
  return instance.request({
    url: `locations/${locationId}/business-profile/basic-information/`,
    method: "PATCH",
    withCredentials: false,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    data,
  });
}

/**
 * Update the Contact & Location card and persist it to Google.
 */
export function updateGoogleBusinessProfileContactLocation(locationId, data) {
  return instance.request({
    url: `locations/${locationId}/business-profile/contact-location/`,
    method: "PATCH",
    withCredentials: false,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    data,
  });
}

/**
 * Update regular opening hours and persist them to Google.
 */
export function updateGoogleBusinessProfileOpeningHours(locationId, data) {
  return instance.request({
    url: `locations/${locationId}/business-profile/opening-hours/`,
    method: "PATCH",
    withCredentials: false,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    data,
  });
}

/**
 * Update supported boolean attributes and persist them to Google.
 */
export function updateGoogleBusinessProfileAttributes(locationId, data) {
  return instance.request({
    url: `locations/${locationId}/business-profile/attributes/`,
    method: "PATCH",
    withCredentials: false,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    data,
  });
}

/**
 * Update the social URL attributes supported by this Google location.
 */
export function updateGoogleBusinessProfileSocialLinks(locationId, data) {
  return instance.request({
    url: `locations/${locationId}/business-profile/social-links/`,
    method: "PATCH",
    withCredentials: false,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    data,
  });
}

export function generateGoogleBusinessProfileDescription(locationId, data) {
  return instance.request({
    url: `locations/${locationId}/business-profile/description-suggestion/`,
    method: "POST",
    withCredentials: false,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    data,
  });
}

export function getGooglePosts(organisationId, locationId) {
  return instance.request({
    url: `locations/${locationId}/posts/?organisation_id=${organisationId}`,
    method: "GET",
    withCredentials: false,
  });
}

export function uploadGooglePosts(locationId, formData) {
  return instance.request({
    url: `locations/${locationId}/posts/`,
    method: "POST",
    withCredentials: false,
    headers: { "Content-Type": "multipart/form-data" },
    data: formData,
  });
}

export function cancelGooglePost(organisationId, locationId, queueItemId) {
  return instance.request({
    url: `locations/${locationId}/posts/${queueItemId}/?organisation_id=${organisationId}`,
    method: "DELETE",
    withCredentials: false,
  });
}

export function updateGooglePostQueueItem(locationId, queueItemId, data) {
  return instance.request({
    url: `locations/${locationId}/posts/${queueItemId}/`,
    method: "PATCH",
    withCredentials: false,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    data,
  });
}

export function updateGooglePostSchedule(locationId, data) {
  return instance.request({
    url: `locations/${locationId}/posts/schedule/`,
    method: "PATCH",
    withCredentials: false,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    data,
  });
}

export function generateGooglePostCaption(locationId, data) {
  return instance.request({
    url: `locations/${locationId}/posts/generate-caption/`,
    method: "POST",
    withCredentials: false,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    data,
  });
}

/**
 * Deactivate Google Reviews integration
 */
export function deactivateGoogleReviews(data) {
  return instance.request({
    url: `deactivate/`,
    method: "POST",
    withCredentials: false,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    data: data,
  });
}

/**
 * Get paginated list of reviews from database
 * @param {Object} params - Query parameters
 * @param {string} params.organisationId - Required organisation ID
 * @param {number[]} params.locationIds - Optional array of location IDs to filter
 * @param {string[]} params.sentiments - Optional array of sentiment filters (positive, neutral, negative)
 * @param {number[]} params.ratings - Optional array of star rating filters (1-5)
 * @param {boolean} params.replied - Optional filter by reply status
 * @param {Date} params.startDate - Optional start date filter
 * @param {Date} params.endDate - Optional end date filter
 * @param {number} params.page - Page number (default 1)
 * @param {number} params.pageSize - Items per page (default 20)
 */
export function getReviewsList(params) {
  const queryParams = new URLSearchParams();
  
  if (params.organisationId) queryParams.append('organisation_id', params.organisationId);
  if (params.locationIds && params.locationIds.length > 0) {
    params.locationIds.forEach(id => queryParams.append('location_id', id));
  }
  // Support multiple sentiments
  if (params.sentiments && params.sentiments.length > 0) {
    params.sentiments.forEach(s => queryParams.append('sentiment', s));
  }
  // Support multiple ratings
  if (params.ratings && params.ratings.length > 0) {
    params.ratings.forEach(r => queryParams.append('rating', r));
  }
  // Support multiple sources
  if (params.sources && params.sources.length > 0) {
    params.sources.forEach(s => queryParams.append('source', s));
  }
  if (params.replied !== undefined) queryParams.append('replied', params.replied);
  if (params.startDate) queryParams.append('start_date', params.startDate.toISOString());
  if (params.endDate) queryParams.append('end_date', params.endDate.toISOString());
  if (params.page) queryParams.append('page', params.page);
  if (params.pageSize) queryParams.append('page_size', params.pageSize);
  if (params.sort) queryParams.append('sort', params.sort);
  
  return instance.request({
    url: `reviews-list/?${queryParams.toString()}`,
    method: "GET",
    withCredentials: false,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  });
}

/**
 * Get dashboard statistics for reviews
 * Uses the same filters as reviews list
 * @param {Object} params - Query parameters
 * @param {string} params.organisationId - Required organisation ID
 * @param {number[]} params.locationIds - Optional array of location IDs to filter
 * @param {string[]} params.sentiments - Optional array of sentiment filters (positive, neutral, negative)
 * @param {number[]} params.ratings - Optional array of star rating filters (1-5)
 * @param {boolean} params.replied - Optional filter by reply status
 * @param {Date} params.startDate - Optional start date filter
 * @param {Date} params.endDate - Optional end date filter
 */
export function getDashboardStats(params) {
  const queryParams = new URLSearchParams();
  
  if (params.organisationId) queryParams.append('organisation_id', params.organisationId);
  if (params.locationIds && params.locationIds.length > 0) {
    params.locationIds.forEach(id => queryParams.append('location_id', id));
  }
  // Support multiple sentiments
  if (params.sentiments && params.sentiments.length > 0) {
    params.sentiments.forEach(s => queryParams.append('sentiment', s));
  }
  // Support multiple ratings
  if (params.ratings && params.ratings.length > 0) {
    params.ratings.forEach(r => queryParams.append('rating', r));
  }
  // Support multiple sources
  if (params.sources && params.sources.length > 0) {
    params.sources.forEach(s => queryParams.append('source', s));
  }
  if (params.replied !== undefined) queryParams.append('replied', params.replied);
  if (params.startDate) queryParams.append('start_date', params.startDate.toISOString());
  if (params.endDate) queryParams.append('end_date', params.endDate.toISOString());
  
  return instance.request({
    url: `dashboard-stats/?${queryParams.toString()}`,
    method: "GET",
    withCredentials: false,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  });
}

export function getCustomerActivity(params) {
  const localDate = value => {
    const date = value instanceof Date ? value : new Date(value);
    const pad = number => String(number).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  };
  const queryParams = new URLSearchParams();
  queryParams.append("organisation_id", params.organisationId);
  (params.locationIds || []).forEach(id => queryParams.append("location_id", id));
  queryParams.append("start_date", localDate(params.startDate));
  queryParams.append("end_date", localDate(params.endDate));
  return instance.get(`customer-activity/?${queryParams.toString()}`, { withCredentials: false });
}

/**
 * Get the latest weekly AI summary for an organisation
 * @param {Object} params - Query parameters
 * @param {string} params.organisationId - Required organisation ID
 * @param {string} [params.weekStart] - Optional ISO week start (YYYY-MM-DD)
 * @param {string} [params.source] - Optional source filter (google, tripadvisor, both)
 */
export function getWeeklyReviewSummary(params) {
  const queryParams = new URLSearchParams();

  if (params.organisationId) queryParams.append('organisation_id', params.organisationId);
  if (params.weekStart) queryParams.append('week_start', params.weekStart);
  if (params.source) queryParams.append('source', params.source);

  return instance.request({
    url: `weekly-summary/?${queryParams.toString()}`,
    method: "GET",
    withCredentials: false,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  });
}

/**
 * Get the earliest review date for an organisation
 * Used to set the minimum date for date picker filters
 * @param {string} organisationId - Required organisation ID
 */
export function getEarliestReviewDate(organisationId) {
  return instance.request({
    url: `earliest-review-date/?organisation_id=${organisationId}`,
    method: "GET",
    withCredentials: false,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  });
}

/**
 * Reply to a review or update an existing reply
 * @param {Object} data - Reply data
 * @param {string} data.organisation_id - Required organisation ID
 * @param {number} data.review_id - Required review ID (database ID)
 * @param {string} data.reply_text - Required reply message
 */
export function replyToReview(data) {
  return instance.request({
    url: `reply/`,
    method: "POST",
    withCredentials: false,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    data: data,
  });
}

/**
 * Delete a reply to a review
 * @param {Object} data - Delete data
 * @param {string} data.organisation_id - Required organisation ID
 * @param {number} data.review_id - Required review ID (database ID)
 */
export function deleteReviewReply(data) {
  return instance.request({
    url: `delete-reply/`,
    method: "POST",
    withCredentials: false,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    data: data,
  });
}

/**
 * Get all AI review settings for an organisation
 * @param {string} organisationId - Required organisation ID
 */
export function getAIReviewSettings(organisationId) {
  return instance.request({
    url: `ai-settings/?organisation_id=${organisationId}`,
    method: "GET",
    withCredentials: false,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  });
}

/**
 * Get a single AI review setting by ID
 * @param {number} settingId - Required setting ID
 * @param {string} organisationId - Required organisation ID
 */
export function getAIReviewSettingDetail(settingId, organisationId) {
  return instance.request({
    url: `ai-settings/${settingId}/?organisation_id=${organisationId}`,
    method: "GET",
    withCredentials: false,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  });
}

/**
 * Create a new AI review setting
 * @param {Object} data - Settings data
 * @param {string} data.organisation_id - Required organisation ID
 * @param {string} data.name - Required name for the setting
 * @param {boolean} data.is_default - Whether this is the default setting
 * @param {number[]} data.location_ids - Array of location IDs to assign
 * @param {boolean} data.auto_reply_enabled - Enable/disable auto-reply
 * @param {string} data.tone_control - Tone setting
 * @param {string} data.persona - Persona setting
 * @param {string} data.default_length - Length setting
 * @param {string} data.greeting_format - Greeting template
 * @param {string} data.signature_format - Signature template
 * @param {string} data.negative_review_apology - Apology script for negative reviews
 * @param {string} data.additional_instructions - Additional instructions
 */
export function createAIReviewSetting(data) {
  return instance.request({
    url: `ai-settings/create/`,
    method: "POST",
    withCredentials: false,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    data: data,
  });
}

/**
 * Update an existing AI review setting
 * @param {Object} data - Settings data
 * @param {string} data.organisation_id - Required organisation ID
 * @param {number} data.setting_id - Required setting ID
 * @param {string} data.name - Name for the setting
 * @param {boolean} data.is_default - Whether this is the default setting
 * @param {number[]} data.location_ids - Array of location IDs to assign
 * @param {boolean} data.auto_reply_enabled - Enable/disable auto-reply
 * @param {string} data.tone_control - Tone setting
 * @param {string} data.persona - Persona setting
 * @param {string} data.default_length - Length setting
 * @param {string} data.greeting_format - Greeting template
 * @param {string} data.signature_format - Signature template
 * @param {string} data.negative_review_apology - Apology script for negative reviews
 * @param {string} data.additional_instructions - Additional instructions
 */
export function saveAIReviewSettings(data) {
  return instance.request({
    url: `ai-settings/save/`,
    method: "POST",
    withCredentials: false,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    data: data,
  });
}

/**
 * Delete an AI review setting
 * @param {number} settingId - Required setting ID
 * @param {string} organisationId - Required organisation ID
 */
export function deleteAIReviewSetting(settingId, organisationId) {
  return instance.request({
    url: `ai-settings/${settingId}/delete/?organisation_id=${organisationId}`,
    method: "DELETE",
    withCredentials: false,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  });
}

/**
 * Generate an AI response for a review
 * @param {Object} data - Generation request data
 * @param {string} data.organisation_id - Required organisation ID
 * @param {number} data.review_id - Required review ID (database ID)
 * @param {string} data.custom_instructions - Optional custom instructions for this response
 */
export function generateReviewResponse(data) {
  return instance.request({
    url: `generate-response/`,
    method: "POST",
    withCredentials: false,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    data: data,
  });
}

/**
 * Generate an AI summary for a review in original language and English translation
 * @param {Object} data - Summary request data
 * @param {string} data.organisation_id - Required organisation ID
 * @param {number} data.review_id - Required review ID (database ID)
 */
export function summarizeReview(data) {
  return instance.request({
    url: `summarize-review/`,
    method: "POST",
    withCredentials: false,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    data: data,
  });
}
